// Daily commitments + team knocks + monthly build goals (Oct 2, 2026).
//
// Replaces the Monday AM/PM/EVE weekly grid. Tue–Fri by 10:00 AM ET the TL or
// ASM confirms each active person's day; Tue–Thu carry team knocks. Monthly
// build goals live in crew_goals (period='month', metric='builds') and the race
// is measured with crew_daily_points.builds — the board's existing "build"
// credit: Chad approved + on the calendar. All dates are ET calendar days.

import { supabase } from "./supabase";
import { etDate, addDaysISO, mondayOf } from "./weeklySpiffs";
import { invokeErrorMessage } from "./edgeError";
import { fetchCalendarBuilds } from "./buildRevenue";

export const CANVASABLE_ROLES = ["restore_manager", "team_lead", "asm", "canvasser"] as const;
/** Tue, Wed, Thu (0=Sun) — the team-knock days. */
export const KNOCK_DOWS = new Set([2, 3, 4]);
/** Tue–Fri — the days that need a 10am confirmation. */
export const CONFIRM_DOWS = new Set([2, 3, 4, 5]);

export type Block = "am" | "pm" | "eve";
export const BLOCKS: Block[] = ["am", "pm", "eve"];
export type Blocks = Record<Block, boolean>;

export interface TeamKnock {
  id: string;
  knock_date: string;
  label: string;
  starts_at: string; // "HH:MM:SS"
  ends_at: string;
  meet_location: string;
  area_label: string | null;
  notes: string | null;
  lead_id: string | null;
  alert_sent_at: string | null;
  cancelled_at: string | null;
}

export interface DailyCommitment {
  id: string;
  user_id: string;
  day: string;
  is_off: boolean;
  off_reason: string | null;
  blocks: Blocks;
  area: string | null;
  team_knock_id: string | null;
  knock_status: "in" | "out" | null;
  knock_out_reason: string | null;
  goal_apps: number | null;
  goal_claims: number | null;
  goal_ia: number | null;
  note: string | null;
  confirmed_by: string | null;
  confirmed_at: string;
  confirmed_late: boolean;
}

export interface DayActuals { apps: number; claims: number; ia: number }

export function todayET(): string {
  return etDate(new Date());
}
/** 0=Sun..6=Sat for a YYYY-MM-DD. */
export function dowOf(day: string): number {
  return new Date(day + "T12:00:00Z").getUTCDay();
}
export function dayLabel(day: string, opts: Intl.DateTimeFormatOptions = { weekday: "short", month: "short", day: "numeric" }): string {
  return new Date(day + "T12:00:00Z").toLocaleDateString("en-US", { timeZone: "UTC", ...opts });
}
/** "4:30 PM" from "16:30:00". */
export function fmtClock(t: string | null | undefined): string {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ap}`;
}
/** Minutes until 10:00 AM ET on `day` (negative once passed). */
export function minutesTo10(day: string, now: Date = new Date()): number {
  const nowDay = etDate(now);
  if (nowDay !== day) return nowDay < day ? Infinity : -Infinity;
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(now);
  const h = Number(parts.find((p) => p.type === "hour")?.value ?? 0);
  const m = Number(parts.find((p) => p.type === "minute")?.value ?? 0);
  return 600 - (h * 60 + m);
}

const roundHalf = (n: number) => Math.round(n * 2) / 2;

/**
 * Default daily goals from a person's weekly goal. Apps lean on the knock days
 * (Tue–Thu 30% each, Fri 10%); claims and signed IAs split evenly Tue–Fri.
 * Rounded to the half because split deals credit 0.5.
 */
export function prefillGoals(
  weekly: { apps?: number; claims?: number; builds_scheduled?: number } | undefined,
  day: string,
): { apps: number; claims: number; ia: number } {
  const dow = dowOf(day);
  const appShare = KNOCK_DOWS.has(dow) ? 0.3 : 0.1;
  return {
    apps: roundHalf((weekly?.apps ?? 0) * appShare),
    claims: roundHalf((weekly?.claims ?? 0) / 4),
    ia: roundHalf((weekly?.builds_scheduled ?? 0) / 4),
  };
}

// ── Reads ────────────────────────────────────────────────────────────────────

export async function fetchKnocks(day: string): Promise<TeamKnock[]> {
  const { data, error } = await supabase
    .from("team_knocks")
    .select("id, knock_date, label, starts_at, ends_at, meet_location, area_label, notes, lead_id, alert_sent_at, cancelled_at")
    .eq("knock_date", day)
    .is("cancelled_at", null)
    .order("label");
  if (error) throw error;
  return (data ?? []) as TeamKnock[];
}

export async function fetchCommitments(day: string): Promise<DailyCommitment[]> {
  const { data, error } = await supabase.from("daily_commitments").select("*").eq("day", day);
  if (error) throw error;
  return (data ?? []).map(normalize);
}

export async function fetchMyCommitment(userId: string, day: string): Promise<DailyCommitment | null> {
  const { data, error } = await supabase
    .from("daily_commitments").select("*").eq("user_id", userId).eq("day", day).maybeSingle();
  if (error) throw error;
  return data ? normalize(data) : null;
}

function normalize(r: Record<string, unknown>): DailyCommitment {
  const num = (v: unknown) => (v == null ? null : Number(v));
  const b = (r.blocks ?? {}) as Partial<Blocks>;
  return {
    ...(r as unknown as DailyCommitment),
    blocks: { am: !!b.am, pm: !!b.pm, eve: !!b.eve },
    goal_apps: num(r.goal_apps),
    goal_claims: num(r.goal_claims),
    goal_ia: num(r.goal_ia),
  };
}

/** Per-person activity on a day (ET), from the same feed the Crew board counts. */
export async function fetchDayActuals(day: string, userIds?: string[]): Promise<Record<string, DayActuals>> {
  let q = supabase.from("crew_daily_points").select("user_id, apps, claims, builds_scheduled").eq("day", day);
  if (userIds?.length) q = q.in("user_id", userIds);
  const { data, error } = await q;
  if (error) throw error;
  const out: Record<string, DayActuals> = {};
  for (const r of data ?? []) {
    out[r.user_id as string] = { apps: Number(r.apps) || 0, claims: Number(r.claims) || 0, ia: Number(r.builds_scheduled) || 0 };
  }
  return out;
}

/** Who was on a canvas shift overlapping each knock's window (ET wall clock). */
export async function fetchKnockShowed(day: string, knocks: TeamKnock[]): Promise<Record<string, Set<string>>> {
  const out: Record<string, Set<string>> = {};
  if (!knocks.length) return out;
  const { data, error } = await supabase
    .from("canvas_shifts")
    .select("user_id, started_at, ended_at")
    .gte("started_at", `${addDaysISO(day, -1)}T12:00:00Z`)
    .lte("started_at", `${addDaysISO(day, 1)}T12:00:00Z`);
  if (error) return out; // attendance is a nice-to-have; never block the board
  const toEtMin = (iso: string) => {
    const d = new Date(iso);
    if (etDate(d) !== day) return null;
    const p = new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(d);
    return Number(p.find((x) => x.type === "hour")?.value) * 60 + Number(p.find((x) => x.type === "minute")?.value);
  };
  const clockMin = (t: string) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
  for (const k of knocks) {
    const ks = clockMin(k.starts_at), ke = clockMin(k.ends_at);
    const set = new Set<string>();
    for (const s of data ?? []) {
      const st = toEtMin(s.started_at as string);
      const en = s.ended_at ? toEtMin(s.ended_at as string) : 24 * 60;
      if (st == null) continue;
      if (st < ke && (en ?? 24 * 60) > ks) set.add(s.user_id as string);
    }
    out[k.id] = set;
  }
  return out;
}

// ── Writes (crew managers; RLS enforces) ─────────────────────────────────────

export type CommitmentInput = Omit<DailyCommitment, "id" | "confirmed_at" | "confirmed_late"> & { confirmed_at?: string };

export async function saveCommitment(input: CommitmentInput): Promise<DailyCommitment> {
  const { data, error } = await supabase
    .from("daily_commitments")
    .upsert(input, { onConflict: "user_id,day" })
    .select("*")
    .single();
  if (error) throw error;
  return normalize(data);
}

export async function deleteCommitment(id: string): Promise<void> {
  const { error } = await supabase.from("daily_commitments").delete().eq("id", id);
  if (error) throw error;
}

export async function saveKnock(k: Partial<TeamKnock> & { knock_date: string; starts_at: string; ends_at: string; meet_location: string }, createdBy: string): Promise<void> {
  const row = { ...k, created_by: k.id ? undefined : createdBy };
  const { error } = k.id
    ? await supabase.from("team_knocks").update(row).eq("id", k.id)
    : await supabase.from("team_knocks").insert(row);
  if (error) throw error;
}

export async function cancelKnock(id: string): Promise<void> {
  const { error } = await supabase.from("team_knocks").update({ cancelled_at: new Date().toISOString() }).eq("id", id);
  if (error) throw error;
}

export async function sendKnockAlert(day: string): Promise<{ sent: number; recipients: number }> {
  const { data, error } = await supabase.functions.invoke("daily-commitments", { body: { mode: "knock_alert", day } });
  if (error) throw new Error(await invokeErrorMessage(error, "Couldn't send the team alert."));
  return data as { sent: number; recipients: number };
}

export interface DaySummaryRecord { sent_at: string; by_name: string | null; open: number }

/** The last "Submit day" summary sent for a day (crew managers can read it). */
export async function fetchDaySummary(day: string): Promise<DaySummaryRecord | null> {
  const { data, error } = await supabase.from("daily_commitment_alerts")
    .select("sent_at, detail").eq("day", day).eq("kind", "summary").maybeSingle();
  if (error || !data) return null;
  const d = (data.detail ?? {}) as { by_name?: string | null; open?: number };
  return { sent_at: data.sent_at as string, by_name: d.by_name ?? null, open: Number(d.open ?? 0) };
}

/** "Submit day" — texts the day's summary to every TL + ASM + Jennifer, Chad, Greg. */
export async function sendDaySummary(day: string): Promise<{ sent: number; recipients: number }> {
  const { data, error } = await supabase.functions.invoke("daily-commitments", { body: { mode: "day_summary", day } });
  if (error) throw new Error(await invokeErrorMessage(error, "Couldn't send the day summary."));
  return { sent: Number(data?.sent ?? 0), recipients: Number(data?.recipients ?? 0) };
}

export async function sendPlanChange(day: string, note: string): Promise<void> {
  const { error } = await supabase.functions.invoke("daily-commitments", { body: { mode: "plan_change", day, note } });
  if (error) throw new Error(await invokeErrorMessage(error, "Couldn't send that to your TL/ASM."));
}

// ── Monthly build goals + race ───────────────────────────────────────────────

export function monthStartOf(day: string): string {
  return day.slice(0, 8) + "01";
}
export function nextMonthStart(monthStart: string): string {
  const [y, m] = monthStart.split("-").map(Number);
  return m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, "0")}-01`;
}
export function monthName(monthStart: string): string {
  return dayLabel(monthStart, { month: "long" });
}
/** The first Monday of the month containing `day`. */
export function firstMondayOf(day: string): string {
  const start = monthStartOf(day);
  const dow = dowOf(start);
  return addDaysISO(start, dow === 1 ? 0 : (8 - dow) % 7);
}
export function isFirstMonday(day: string): boolean {
  return firstMondayOf(day) === day;
}
/** Weekdays in the month, and how many have started by `day` (inclusive). */
export function businessDays(monthStart: string, day: string): { total: number; elapsed: number } {
  let total = 0, elapsed = 0;
  for (let d = monthStart; d < nextMonthStart(monthStart); d = addDaysISO(d, 1)) {
    const w = dowOf(d);
    if (w === 0 || w === 6) continue;
    total++;
    if (d <= day) elapsed++;
  }
  return { total, elapsed };
}

export async function fetchMonthBuildGoals(monthStart: string): Promise<Record<string, number>> {
  const { data, error } = await supabase
    .from("crew_goals")
    .select("user_id, target")
    .eq("scope", "personal").eq("period", "month").eq("metric", "builds")
    .eq("period_start", monthStart);
  if (error) throw error;
  const out: Record<string, number> = {};
  for (const r of data ?? []) out[r.user_id as string] = Number(r.target);
  return out;
}

export async function setMonthBuildGoal(userId: string, monthStart: string, target: number, createdBy: string): Promise<void> {
  if (target <= 0) {
    const { error } = await supabase.from("crew_goals").delete()
      .eq("scope", "personal").eq("period", "month").eq("metric", "builds")
      .eq("period_start", monthStart).eq("user_id", userId);
    if (error) throw error;
    return;
  }
  const { error } = await supabase.from("crew_goals").upsert(
    { scope: "personal", user_id: userId, period: "month", metric: "builds", target, period_start: monthStart,
      created_by: createdBy, created_at: new Date().toISOString() },
    { onConflict: "scope,user_id,period,period_start,metric" },
  );
  if (error) throw error;
}

/** Builds credited per person in [monthStart, nextMonth) — paged past the row cap. */
/**
 * Builds this month per person — builds ON THE CALENDAR for the month (first
 * build day inside it), owner 1 or 0.5 each on a split. Oct 27, 2026: was the
 * crew `build` credit (crew_daily_points.builds), which is stamped from the
 * preferred build date at midnight UTC and so counted a build on the 1st in
 * the month before. Greg: count the builds scheduled for the period.
 */
export async function fetchMonthBuilds(monthStart: string): Promise<Record<string, number>> {
  return fetchCalendarBuilds(monthStart, nextMonthStart(monthStart));
}

/** Same-week Monday helper re-exported for callers that already import this module. */
export { mondayOf };
